import { Check } from '@insify/design-system/icons';
import { Email, ShieldCheck } from '@insify/design-system/line-icons';
import { textMdSemibold } from '@insify/design-system/typography';
import {
  CoBrandingLogoConfirmation,
  Confirmation,
  PartnerDeals,
  RichContentWithTooltip,
  finomImageSrc,
  hasher,
  useProspect,
} from '@insify/funnel-components';
import clsx from 'clsx';
import { useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import { useCoBranding } from '../../hooks/useCoBranding';
import { useContent } from '../../hooks/useContent';
import { useCoverageValues } from '../../hooks/useCoverageValues';
import { useParamsQuery } from '../../hooks/useQueries';
import { GTMEvent, useTagManager } from '../../hooks/useTagManager';
import { PROSPECT_STATUS_QUERY_KEY } from '../../models/queries';

import styles from './Confirmation.module.css';
import { CrossSellModal } from './components/CrossSellModal';
import IonosLogo from './ionos_logo.svg';

import type { PartnerDeal } from '@insify/funnel-components';

const useInvalidateProspectStatusOnUnmount = () => {
  const prospectId = useProspect();
  const queryClient = useQueryClient();
  const { sendEvent, sendPurchaseEvent } = useTagManager();
  const { data } = useParamsQuery();
  const coverageValues = useCoverageValues();

  const sendGTMEvent = async (): Promise<void> => {
    const email = data?.representative?.email !== undefined ? await hasher('SHA-256', data.representative.email) : '';
    sendEvent(GTMEvent.PROPERTY_AND_CASUALTY_PURCHASE, { email });

    const premiumInCents = Number(coverageValues.premiums.total?.amount?.cents || 0);
    const pncConversionValue = (premiumInCents / 100) * 12;
    const hashedProspectId = await hasher('SHA-256', prospectId);

    sendEvent(GTMEvent.DAISYCON_PNC_PURCHASE, {
      pncConversionValue: String(pncConversionValue),
      transactionId: hashedProspectId,
      compensationCode: 'rcpro',
    });

    sendPurchaseEvent({
      transaction_id: hashedProspectId,
      value: pncConversionValue,
    });
  };

  useEffect(() => {
    void sendGTMEvent();

    return () => {
      void queryClient.invalidateQueries([PROSPECT_STATUS_QUERY_KEY]);
    };
  }, []);
};

export const ConfirmationPage = () => {
  const { confirmation } = useContent() || {};
  const { t } = useTranslation(['funnel']);
  const { hasCoBrandingLoaded, branding, hasCoBranding } = useCoBranding();
  useInvalidateProspectStatusOnUnmount();

  const Header = ({ className }: { className: string }) => (
    <RichContentWithTooltip content={confirmation} className={className} />
  );

  return (
    <>
      <Confirmation
        illustration={<Check className={styles.illustration} />}
        Header={Header}
        nextStepsHeader={t('funnel:confirm_next_steps_header')}
        nextSteps={[
          {
            icon: <Email />,
            body: <Trans i18nKey='funnel:confirm_next_steps_email' />,
          },
          {
            icon: <ShieldCheck />,
            body: t('funnel:confirm_next_steps_secure'),
          },
        ]}
        logo={
          hasCoBrandingLoaded
            ? ({ className }) => <CoBrandingLogoConfirmation branding={branding} className={className} />
            : undefined
        }
        slotAfterLogo={hasCoBranding ? null : <PartnerDealsSection />}
      />
      <CrossSellModal trackingId='cross-sell-health' />
    </>
  );
};

const PartnerDealsSection = () => {
  const { t } = useTranslation(['funnel']);

  const partnerDeals: PartnerDeal[] = [
    {
      identifier: 'ionos',
      Image: ({ className }) => (
        <div className={clsx(className, styles.ionosLogo)}>
          <IonosLogo />
        </div>
      ),
      heading: t('funnel:partner_deal_ionos_heading'),
      description: t('funnel:partner_deal_ionos_description'),
      cta: t('funnel:partner_deal_ionos_cta'),
      link: 'https://www.ionos.fr/site-internet/site-internet-avec-nom-de-domaine-insify',
    },
    {
      identifier: 'finom',
      Image: ({ alt, className }) => <img src={finomImageSrc} alt={alt} className={className} />,
      heading: t('funnel:partner_deal_finom_heading'),
      description: t('funnel:partner_deal_finom_description'),
      cta: t('funnel:partner_deal_finom_cta'),
      link: 'https://finom.fr/?utm_source=partner&utm_medium=cpp&utm_campaign=Insify-banking-.fr-France&fnm_promo=3freemonths&fnm_partner=Insify',
    },
  ];

  return (
    <div className={styles.partnerDealsSection}>
      <p className={clsx(textMdSemibold, styles.partnerDealsHeading)}>{t('funnel:partner_deal_heading')}</p>
      <PartnerDeals partnerDeals={partnerDeals} />
    </div>
  );
};
